import { useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText } from '../../../../../styling/stylingHelper';
import { inspectProductQuantities, findGmailAccount, type FlaggedProduct } from '../../../../../services/admin/adminService';
import { Check, Copy } from 'lucide-react';
import { buildFixQuantitiesPrompt } from './quantityPrompt';

export function PromptCopyCell({ product }: { product: FlaggedProduct }) {
  const [state, setState] = useState<'idle' | 'loading' | 'copied' | 'error'>('idle');

  const handleClick = async () => {
    if (state === 'loading') return;
    setState('loading');
    try {
      const { rows, inventory, costPrice } = await inspectProductQuantities(product.productName, {
        platform: product.platform,
        articleId: product.articleId,
      });
      const prompt = buildFixQuantitiesPrompt(product, rows, inventory, costPrice);
      await navigator.clipboard.writeText(prompt);
      setState('copied');
      setTimeout(() => setState('idle'), 1500);
    } catch (err) {
      console.error('Failed to build fix-quantities prompt', err);
      setState('error');
      setTimeout(() => setState('idle'), 2000);
    }
  };

  const label = state === 'loading' ? '…' : state === 'copied' ? '✓' : state === 'error' ? '✕' : '⧉';
  const color =
    state === 'copied' ? 'text-green-600 dark:text-green-400'
    : state === 'error' ? 'text-red-500'
    : 'text-gray-400 hover:text-blue-600 dark:hover:text-blue-400';

  return (
    <button
      onClick={handleClick}
      disabled={state === 'loading'}
      title="Copy fix-quantities prompt (includes DB inspection)"
      className={cn('transition-colors', color)}
    >
      {label}
    </button>
  );
}

export function PlatformCopyCell({ platform, gmailId }: { platform: string; gmailId: string | null }) {
  const [copied, setCopied] = useState(false);
  const [account, setAccount] = useState<'cloud63' | 'cloud64' | null>(null);
  const [accountState, setAccountState] = useState<'idle' | 'loading' | 'done'>('idle');

  if (!gmailId) {
    return <span>{platform}</span>;
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(`rfc822msgid:${gmailId}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    // Resolve which mailbox (63/64) holds this message — only the first time.
    if (accountState === 'idle') {
      setAccountState('loading');
      try {
        setAccount(await findGmailAccount(gmailId));
      } catch {
        setAccount(null);
      } finally {
        setAccountState('done');
      }
    }
  };

  const accountBadge =
    accountState === 'loading' ? '…'
    : account === 'cloud63' ? '63'
    : account === 'cloud64' ? '64'
    : accountState === 'done' ? '?'
    : null;

  return (
    <span className="inline-flex items-center gap-1">
      <button
        onClick={handleCopy}
        title="Copy rfc822msgid + find Gmail account (63/64)"
        className="cursor-pointer hover:underline text-blue-600 dark:text-blue-400"
      >
        {copied ? 'Copied!' : platform}
      </button>
      {accountBadge && (
        <span
          className={cn(
            'text-[10px] px-1.5 py-0.5 rounded-full font-semibold',
            account === 'cloud63' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
            : account === 'cloud64' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
            : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
          )}
          title={account ? `culinarycloud${account === 'cloud63' ? '63' : '64'}@gmail.com` : 'Not found in either inbox'}
        >
          {accountBadge}
        </span>
      )}
    </span>
  );
}

// Key/value row for a review card body.
export function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className={secondaryText}>{label}</span>
      <span className={cn(primaryText, 'text-right truncate')} title={value}>{value}</span>
    </div>
  );
}

export function MsgIdCopyCell({ rfc822MessageId, label, iconOnly }: { rfc822MessageId: string | null; label: string; iconOnly?: boolean }) {
  const [copied, setCopied] = useState(false);
  if (!rfc822MessageId) {
    return iconOnly ? null : <span title="No Message-ID header available">{label}</span>;
  }
  const handleCopy = async () => {
    await navigator.clipboard.writeText(`rfc822msgid:${rfc822MessageId}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  if (iconOnly) {
    return (
      <button
        onClick={handleCopy}
        title="Copy rfc822msgid: search to clipboard"
        className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    );
  }
  return (
    <button
      onClick={handleCopy}
      title="Copy rfc822msgid: search to clipboard"
      className="font-mono hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
    >
      {copied ? 'Copied!' : label}
    </button>
  );
}
